import { useCallback, useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import { useDistributorWorkspace } from '@/contexts/DistributorWorkspaceContext'
import {
  buildMyDistributorAffiliateLink,
  fetchMyDistributorAffiliates,
  formatMyDistributorCurrency,
} from '@/lib/my-distributor'
import { RequireDistributorWorkspace } from '@/pages/SouDistribuidorPage/RequireDistributorWorkspace'
import type { MyDistributorAffiliateRegistration } from '@/types/my-distributor'
import styles from './SouDistribuidorPage.module.css'

function filterCampaignsForRegistration(
  registration: MyDistributorAffiliateRegistration,
  campaignId: number | null
) {
  if (!registration.is_active) {
    return []
  }
  const campaigns = registration.allowed_campaigns
  if (campaignId == null) {
    return campaigns
  }
  if (registration.all_campaigns) {
    return campaigns.filter((campaign) => campaign.id === campaignId)
  }
  return campaigns.filter((campaign) => campaign.id === campaignId)
}

function SouDistribuidorAfiliadosLinksPageContent() {
  const { selectedProgram, campaignId } = useDistributorWorkspace()
  const [registrations, setRegistrations] = useState<MyDistributorAffiliateRegistration[]>([])
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!selectedProgram) return
    setIsLoading(true)
    setError(null)
    try {
      const res = await fetchMyDistributorAffiliates(selectedProgram.distributor_id, {
        campaign_id: campaignId ?? undefined,
      })
      setRegistrations(res.registrations.filter((registration) => registration.is_active))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar links dos afiliados.')
    } finally {
      setIsLoading(false)
    }
  }, [selectedProgram, campaignId])

  useEffect(() => {
    void load()
  }, [load])

  const copyLink = (key: string, url: string) => {
    void navigator.clipboard.writeText(url).then(() => {
      setCopiedKey(key)
      window.setTimeout(() => setCopiedKey(null), 2000)
    })
  }

  if (isLoading) {
    return (
      <div className={styles.centered}>
        <Icon icon="mdi:loading" className={styles.spinner} />
        <span>Carregando...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.centered}>
        <Icon icon="mdi:alert-circle-outline" className={styles.errorIcon} />
        <span>{error}</span>
        <button type="button" className={styles.retryBtn} onClick={() => void load()}>
          Tentar novamente
        </button>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <p className={styles.filterLabel}>
        Links dos afiliados credenciados por você em{' '}
        <strong>{selectedProgram?.institution.name}</strong>. O parâmetro <code>af</code> identifica
        cada afiliado no checkout.
      </p>

      {registrations.length === 0 ? (
        <div className={styles.emptyBlock}>
          <Icon icon="mdi:link-off" />
          <span>Nenhum afiliado ativo para exibir links neste contexto.</span>
        </div>
      ) : (
        <ul className={styles.linkList}>
          {registrations.map((registration) => {
            const campaigns = filterCampaignsForRegistration(registration, campaignId)

            return (
              <li key={registration.id} className={styles.programBlock}>
                <div className={styles.programBlockHead}>
                  <div>
                    <h3 className={styles.programBlockTitle}>{registration.affiliate_name}</h3>
                    <span className={styles.trackingCode}>
                      Código: {registration.tracking_code}
                    </span>
                  </div>
                  <span className={styles.badgeActive}>Ativo</span>
                </div>

                {campaigns.length === 0 ? (
                  <p className={styles.programMeta}>
                    Nenhuma campanha disponível para gerar link neste filtro.
                  </p>
                ) : (
                  <div className={styles.campaignRows}>
                    {campaigns.map((campaign) => {
                      const url = buildMyDistributorAffiliateLink(
                        campaign,
                        registration.tracking_code
                      )
                      const key = `${registration.id}-${campaign.id}`
                      const label = campaign.title?.trim() || campaign.name
                      const percentLabel =
                        campaign.commission_percent !== undefined
                          ? ` · ${campaign.commission_percent}%`
                          : registration.all_campaigns
                            ? ` · ${registration.commission_percent}%`
                            : ''

                      return (
                        <div key={key} className={styles.campaignRow}>
                          <span className={styles.campaignName}>
                            {label}
                            {percentLabel}
                          </span>
                          <input
                            type="text"
                            className={styles.linkInput}
                            value={url}
                            readOnly
                            aria-label={`Link da campanha ${label}`}
                          />
                          <button
                            type="button"
                            className={`${styles.copyBtn} ${copiedKey === key ? styles.copied : ''}`}
                            onClick={() => copyLink(key, url)}
                          >
                            <Icon icon={copiedKey === key ? 'mdi:check' : 'mdi:content-copy'} />
                            {copiedKey === key ? 'Copiado' : 'Copiar'}
                          </button>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.openLinkBtn}
                          >
                            <Icon icon="mdi:open-in-new" />
                            Abrir
                          </a>
                        </div>
                      )
                    })}
                  </div>
                )}

                {registration.metrics ? (
                  <p className={styles.programMeta} style={{ marginTop: 'var(--spacing-sm)' }}>
                    {registration.metrics.payments_count} vendas ·{' '}
                    {formatMyDistributorCurrency(registration.metrics.total_sales)}
                  </p>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export function SouDistribuidorAfiliadosLinksPage() {
  return (
    <RequireDistributorWorkspace requireActiveProgram>
      <SouDistribuidorAfiliadosLinksPageContent />
    </RequireDistributorWorkspace>
  )
}
